import { cupResultText } from './cup/advance'
import { cupReportScore, scoreCupReport, type CupReportInput } from './cup/report'
import { crewCreditGaps, type CardCrewSlot, type CrewCreditLine } from './crewCredit'
import { deriveResult, type ScoreInput } from './gameResult'
import type { RefPosition } from './index'
import { otherArPosition } from './refereeListing'

/** What the statistician's exception pass reads. Flags are raised INSIDE the
 *  approve batch, so the trace and the edit cannot disagree.
 *
 *  ⚠️ A MISSING CARD IS NOT A FLAG. A game with no card scores a full 5 for
 *  both teams by ruling, because the rated team does not control whether the
 *  referee submitted. Missing cards are a Missing Cards read, where they are a
 *  missing SCORE to chase. */
export type ReviewFlagKind =
  | 'cards_disagree'
  | 'result_mismatch'
  | 'goals_unreconciled'
  | 'blowout'
  | 'red_card'
  | 'injury'
  | 'incident_note'
  | 'credit_unresolved'
  | 'matrix_incomplete'
  | 'reidentified'
  | 'card_misfiled'
  | 'crew_mismatch'
  | 'yellow_card'
  | 'coach_card'
  | 'referee_report_mismatch'
  | 'incident_checked'
  | 'incident_reported'

export interface ReviewFlag {
  kind: ReviewFlagKind
  detail: Record<string, unknown>
}

export interface ReviewPlayerMark {
  childId: string
  goals?: number
  card?: 'yellow' | 'red' | null
  absent?: boolean
  injured?: boolean
  note?: string | null
}

export interface ReviewTeamEntry {
  teamId: string
  side: 'home' | 'visitor'
  cellsTotal: number
  cellsRated: number
  always: number
  rarely: number
  incidentNote: string | null
  players: ReviewPlayerMark[]
  /** The card back's two boxes as the reviewer recorded them for this team
   *  (`game_sportsmanship.ref_treatment_json`). Optional: an older caller that
   *  omits it reads as "nothing ticked", which raises nothing. */
  refTreatment?: { injuryOrMisconduct?: boolean; refereeMistreated?: boolean }
  /** Coach yellow and red cards the referee wrote on THIS team's card
   *  (owner, 2026-10-07): 1 point each yellow, 3 each red, off computed
   *  points. Optional on the wire: an older caller that omits it reads as
   *  none. ⚠️ Every hop of the review round trip carries it (spec §6.2);
   *  a hop that drops it writes 0 back on an override, the way
   *  `ref_treatment_json` was once lost. */
  coachCards?: { yellow: number; red: number }
}

/** One crew slot as the reviewer recorded it: the credit line
 *  (`CrewCreditLine`: letter, printed name, credited team or Region 13 at
 *  large) plus which slot it is and who the name was matched to.
 *
 *  ⚠️ `creditedTeamId` AND `creditedAtLarge` ARE MUTUALLY EXCLUSIVE (0062's own
 *  CHECK on `game_referee_credits`). Neither set means "nobody credited yet";
 *  at large means the reviewer deliberately chose Region 13. The route refuses
 *  a body carrying both (`firstInvalidCrewField`, `apps/stats/src/reviewRoutes.ts`). */
export interface ReviewCrewEntry extends CrewCreditLine {
  slot: CardCrewSlot
  resolvedPersonId: string | null
}

/** What the referee scheduler expects for one of a game's three positions,
 *  narrowed to the three fields `crew_mismatch` actually compares — see
 *  `dao/expectedCrew.ts` (`ExpectedCrewSlot`) for the full DB-backed shape
 *  this is derived from. `responsibility` (team external_id or `'Region'`)
 *  is NOT carried in here: the comparison this type feeds is person-vs-person,
 *  not team-vs-team.
 *
 *  ⚠️ `assigneeEmail` IS CARRIED, DELIBERATELY — a corrected ruling. An
 *  earlier version of this type dropped it on the theory that an id was
 *  always enough, but `referee_assignments.assignee_user_id` is nullable
 *  (0028_referee_assignments.sql: InLeague hasn't linked that referee yet)
 *  while `email` on the same row is NOT NULL. Without `assigneeEmail`, a
 *  slot the scheduler filled with a real, known, email-only referee read
 *  identically to one nobody ever claimed — see `deriveReviewFlags` for how
 *  the two are told apart now, and how a card-side and scheduler-side
 *  identity of DIFFERENT kinds (one an id, one an email) are compared.
 *
 *  `position` is the full `RefPosition` union, not narrowed to `'CR'|'AR1'|
 *  'AR2'`: it is fed straight from `ExpectedCrewSlot` (`dao/expectedCrew.ts`),
 *  which widened for the same reason (2026-09-08) — AR3 is effectively
 *  unreachable here today because `GAME_CARD_MIN_AGE = 10` keeps a young
 *  game's card out of review entirely, but a type that claims it cannot
 *  happen is worse than one that says it can. */
export interface ReviewExpectedCrewSlot {
  position: RefPosition
  assigneeUserId: string | null
  assigneeEmail: string | null
  /** `referee_assignments.state` on the slot's most recent assignment
   *  ('assigned' | 'confirmed' | 'cancelled'), or null when the slot has
   *  never had one. A 'cancelled' assignment means the assignee backed out,
   *  so it is treated as unfilled — see `deriveReviewFlags`. */
  state: string | null
}

export interface ReviewFlagInput {
  score: ScoreInput
  /** WHAT THE CARD ITSELF SAYS, as the model read the ticked result box —
   *  mapped onto home/visitor/tie using that face's `declaredSide`. Null when
   *  the box was blank, unreadable, or the face did not say which side it was.
   *
   *  ⚠️ THIS USED TO BE THE REVIEWER'S OWN DROPDOWN, AND THAT MADE THE FLAG
   *  BELOW UNABLE TO FIRE FOR A REAL REASON. It compared a selection the
   *  reviewer made against goals the same reviewer had just typed — two
   *  expressions of one judgment, so a disagreement only ever meant a slip
   *  between two of their own keystrokes, and a reviewer who left the dropdown
   *  alone (the guard is `if (cardResult && …)`) skipped the check entirely.
   *  The one game reviewed in production did exactly that.
   *
   *  The ticked box is genuinely independent: a volunteer referee wrote it in a
   *  car park, the model transcribed it without reconciling, and the reviewer
   *  typed the goals from the grid. The extraction prompt is explicit that this
   *  contradiction is the point — "If the score grid says 2-1 and the result
   *  box is ticked Tie, record both" — so surfacing it is the flag finally
   *  doing the job it was named for. Owner ruling 2026-09-13. */
  cardBoxResult: 'home' | 'visitor' | 'tie' | null
  /** The other card's regulation score for this game, when a second card exists. */
  otherCardScore: { home: number; visitor: number } | null
  reidentified: boolean
  crew: ReviewCrewEntry[]
  teams: ReviewTeamEntry[]
  /** What the referee scheduler expected for this game's crew, or absent
   *  entirely when the caller has no such evidence. ⚠️ ABSENT IS NOT A
   *  MISMATCH — see `deriveReviewFlags`'s crew loop. Distinct from an empty
   *  array, which means the scheduler is confidently asserting there was
   *  nothing to expect (a division `referee_slots` never covers, or a game
   *  with no slot rows at all) — both read as "raise nothing" today, but for
   *  different reasons, and only one of them is a caller admitting it
   *  doesn't know. */
  expectedCrew?: ReviewExpectedCrewSlot[]
  /** The upload wizard's two incident answers, folded across every
   *  non-rejected submission of the game by `foldUploadIncidents`. ⚠️ READ SERVER-SIDE
   *  (`toFlagInput`), never from the approve request. Absent or null means
   *  never asked, which raises nothing. */
  uploadIncidents?: { player: boolean | null; referee: boolean | null }
}

const BLOWOUT_MARGIN = 6

export type FlagTier = 'problem' | 'sporting' | 'data' | 'routine'

/** ⚠️ THE ONE PLACE A KIND IS SORTED. The Exceptions page, the server's viewer
 *  filter and the Monday digest all read this; a second list would drift.
 *  `Record<ReviewFlagKind, …>` makes a new kind fail typecheck until placed.
 *  Owner, 2026-09-27. */
export const FLAG_TIER: Record<ReviewFlagKind, FlagTier> = {
  red_card: 'problem',
  yellow_card: 'problem',
  coach_card: 'problem',
  referee_report_mismatch: 'data',
  injury: 'problem',
  incident_note: 'problem',
  incident_checked: 'problem',
  incident_reported: 'problem',
  blowout: 'sporting',
  cards_disagree: 'data',
  result_mismatch: 'data',
  goals_unreconciled: 'data',
  credit_unresolved: 'data',
  matrix_incomplete: 'data',
  reidentified: 'data',
  card_misfiled: 'data',
  crew_mismatch: 'routine',
}

/** The kinds a Division Referee Administrator owns (spec 2026-10-12 B1). */
export const INCIDENT_FLAG_KINDS: ReviewFlagKind[] = (Object.keys(FLAG_TIER) as ReviewFlagKind[]).filter((k) => FLAG_TIER[k] === 'problem')

/** Closes a flag at approve that needs no decision. */
export const INFO_RESOLUTION = 'info'

/** ⚠️ THE ONE SPELLING of what closes a flag on a correction. `listWeekFlags` and the
 *  digest drop flags closed this way by matching this value. */
export const SUPERSEDED_RESOLUTION = 'superseded by correction'

/** The flags that also mail the division's aliases the moment a card is reviewed. */
export const ALERT_FLAG_KINDS: ReviewFlagKind[] = ['red_card', 'coach_card', 'injury']

export const isCoachFlag = (kind: string) => kind === 'coach_card'

/** One or two cautions on a team in a game are information, shown and closed
 *  (spec B1). A third needs a decision. A second caution to one player is a
 *  red on the card (a mark holds one card), so it arrives as `red_card`. */
export function isInformationalFlag(flag: ReviewFlag): boolean {
  if (flag.kind !== 'yellow_card') return false
  const children = Array.isArray(flag.detail.children) ? flag.detail.children : []
  return children.length < 3
}

export const FLAG_TIER_ORDER: FlagTier[] = ['problem', 'sporting', 'data', 'routine']

/** `game_review_flags.kind` is free text to a reader, so an unknown kind lands
 *  in `data`, where the stats lead sees it, rather than vanishing. */
export function flagTier(kind: string): FlagTier {
  return FLAG_TIER[kind as ReviewFlagKind] ?? 'data'
}

/** Every kind `deriveReviewFlags` can raise, in words. The stats SPA and the
 *  Monday digest both read this; a second list would drift.
 *
 *  ⚠️ TYPED AS THE FULL `ReviewFlagKind` UNION ON PURPOSE. Adding a kind above
 *  and forgetting this map is then a compile error rather than a row that reads
 *  `some_new_kind` on screen. The runtime fallback in `flagKindLabel` still
 *  exists, because `game_review_flags.kind` is a free-text column and an older
 *  row can hold a kind this build no longer declares. */
export const FLAG_KIND_LABELS: Record<ReviewFlagKind, string> = {
  cards_disagree: 'The two cards disagree',
  result_mismatch: 'Win/loss does not match the score',
  goals_unreconciled: 'Marked goals do not match the score',
  blowout: 'Blowout deduction fired',
  red_card: 'Red card',
  injury: 'Injury',
  incident_note: 'Incident note',
  credit_unresolved: 'Referee credit error',
  matrix_incomplete: 'Behavior matrix left incomplete',
  reidentified: 'Card moved to a different game',
  card_misfiled: 'Card marked as another game’s',
  crew_mismatch: 'Crew does not match the schedule',
  yellow_card: 'Yellow card',
  coach_card: 'Coach card',
  referee_report_mismatch: 'Referee report does not match the card',
  incident_checked: 'Incident box checked on card',
  incident_reported: 'Incident reported at upload',
}

export function flagKindLabel(kind: string): string {
  return FLAG_KIND_LABELS[kind as ReviewFlagKind] ?? kind
}

/** What an `incident_reported` flag is called. The upload answers can report
 *  an incident the card itself does not show, and that case is the one a
 *  reader most needs to see, so it gets its own wording. */
export function incidentReportedLabel(detail: Record<string, unknown>): string {
  return detail.onCard === false ? 'Reported at upload, not on the card' : 'Incident reported at upload'
}

/** The team a blowout's stored `winner` side names, as its designation
 *  ("B12-03"). Null for anything but home or visitor. */
export function blowoutWinnerLabel(winner: unknown, home: string, away: string): string | null {
  if (winner === 'home') return home
  if (winner === 'visitor') return away
  return null
}

/** "Blowout: B12-03 won by 8". Null when the winner or margin is unknown, so
 *  the caller keeps its generic wording rather than printing a blank. */
export function blowoutLabel(winnerLabel: unknown, margin: unknown): string | null {
  if (typeof winnerLabel !== 'string' || !winnerLabel || typeof margin !== 'number') return null
  return `Blowout: ${winnerLabel} won by ${margin}`
}

/** The reviewer's typed score against a Cup game's confirmed result, on goals, PKs and
 *  forfeit, or null when they agree. `notation` says all three, and `deriveResult` reads
 *  a forfeit by its side, whatever goals the card holds.
 *
 *  ⚠️ `cup` IS WHAT TELLS THIS FLAG FROM THE CARD BOX'S OWN `result_mismatch`, which says
 *  nothing about the confirmed result. A correction on the Cup page recomputes this one
 *  and leaves that one. It changes nothing else: the confirmed result stands. */
export function cupResultMismatch(confirmed: CupReportInput, onCard: ScoreInput): ReviewFlag | null {
  if (deriveResult(cupReportScore(confirmed)).notation === deriveResult(onCard).notation) return null
  return { kind: 'result_mismatch', detail: { cup: true, confirmed: cupResultText(confirmed), onCard: cupResultText(scoreCupReport(onCard)) } }
}

/** What one flag is called on the Exceptions and Cup pages. Three kinds read their own
 *  detail: an incident the card does not show, who a blowout favored, and a Cup card
 *  against its confirmed result. */
export function flagTitle(kind: string, detail: Record<string, unknown>): string {
  if (kind === 'incident_reported') return incidentReportedLabel(detail)
  if (kind === 'result_mismatch' && detail.cup === true) return 'The card disagrees with the confirmed result'
  if (kind === 'blowout') return blowoutLabel(detail.winnerLabel, detail.margin) ?? flagKindLabel(kind)
  return flagKindLabel(kind)
}

/** Any Yes wins, else any No, else null (never asked), per question. */
export function foldUploadIncidents(
  rows: { player: 0 | 1 | null; referee: 0 | 1 | null }[],
): { player: boolean | null; referee: boolean | null } {
  const fold = (vals: (0 | 1 | null)[]) =>
    vals.includes(1) ? true : vals.includes(0) ? false : null
  return { player: fold(rows.map((r) => r.player)), referee: fold(rows.map((r) => r.referee)) }
}

/** Every goal the reviewer attributed to a named player on one side. */
export function markedGoals(players: ReviewPlayerMark[]): number {
  return players.reduce((n, p) => n + (p.goals ?? 0), 0)
}

/** Whether a team's per-player goals contradict its final — the rule behind
 *  the `goals_unreconciled` flag, and behind the warning the review form shows
 *  before an approve.
 *
 *  ⚠️ ZERO MARKED MEANS NOT RECORDED, NOT RECORDED-AS-ZERO. A reviewer who
 *  skipped the roster entirely has contradicted nothing, and raising on them
 *  would fire on most cards — the fastest way to make a whole flag kind
 *  ignored. So the rule stays deliberately under-raising: a team whose scorers
 *  were never written down passes silently, in both the flag and the warning.
 *
 *  ⚠️ EXPORTED SO THE SCREEN AND THE RECORD CANNOT DISAGREE. The warning the
 *  reviewer reads before pressing Approve and the flag raised inside the
 *  approve batch are the same question asked twice, seconds apart; a second
 *  copy of this comparison would eventually warn about a card it then filed
 *  clean, or file one it never warned about. Same class as the crew-order
 *  comparator that lost `AR3` in a private copy (CLAUDE.md). */
export function goalsUnreconciled(marked: number, final: number): boolean {
  return marked > 0 && marked !== final
}

/** ⚠️ A COMPARISON-SAFETY HEURISTIC, NOT AN IDENTITY SYSTEM. Used only to
 *  decide whether `crew_mismatch`'s comparison is even ALLOWED to run — see
 *  its call site. A wrong guess here only ever pushes towards staying quiet
 *  (a real match misread as "different kinds" just means no flag), never
 *  towards a false raise, which is why a plain `@` check is an acceptable
 *  risk here even though `dao/crewSearch.ts`'s `CrewPersonIdType` exists
 *  because the two shapes are not reliably distinguishable in general — that
 *  discriminator answers a different question (what IS this string), this
 *  one only answers "can I compare this string against an email". */
function looksLikeEmail(id: string): boolean {
  return id.includes('@')
}

/** Who the scheduler expects in a slot, and by which kind of identity: the
 *  InLeague id when there is one, else the email. Null for a slot never
 *  filled or backed out of, which is no expectation at all. */
function expectedIdentity(e: ReviewExpectedCrewSlot): { kind: 'id' | 'email'; value: string } | null {
  if (e.state === 'cancelled') return null
  if (e.assigneeUserId != null) return { kind: 'id', value: e.assigneeUserId }
  if (e.assigneeEmail != null) return { kind: 'email', value: e.assigneeEmail }
  return null
}

export function deriveReviewFlags(input: ReviewFlagInput): ReviewFlag[] {
  const flags: ReviewFlag[] = []
  const derived = deriveResult(input.score)

  // ⚠️ ABSENT IS NOT A MISMATCH. A blank or unreadable result box, or a face
  // that never said which side it was, means we have nothing to compare — not
  // that the card agrees.
  if (input.cardBoxResult && input.cardBoxResult !== derived.outcome) {
    flags.push({ kind: 'result_mismatch', detail: { selected: input.cardBoxResult, derived: derived.outcome } })
  }

  if (
    input.otherCardScore &&
    (input.otherCardScore.home !== input.score.homeGoalsReg ||
      input.otherCardScore.visitor !== input.score.visitorGoalsReg)
  ) {
    flags.push({
      kind: 'cards_disagree',
      detail: {
        thisCard: { home: input.score.homeGoalsReg, visitor: input.score.visitorGoalsReg },
        otherCard: input.otherCardScore,
      },
    })
  }

  // ⚠️ READ OFF THE SCORE, not carried beside it. A separate `isForfeit` boolean
  // on this input was a second representation of `score.forfeitSide`, and two
  // representations of one fact is how `reconcile_status` got lost.
  const isForfeit = input.score.forfeitSide !== null
  const margin = Math.abs(derived.homeFinal - derived.visitorFinal)
  // ⚠️ THE FORFEIT TERM IS NOW UNREACHABLE BY CONSTRUCTION, AND IT STAYS. Since
  // `deriveResult` caps a forfeit's final at 1-0 or 0-0, the margin can never
  // exceed BLOWOUT_MARGIN on a forfeited game, so this condition cannot change
  // the outcome today. It is kept because it states the RULE — a forfeit is an
  // administrative result, not conduct — and because the margin's source is one
  // edit away from being the raw goals again, which would make it load-bearing
  // immediately. Recorded here rather than pinned by a test that cannot fail.
  if (!isForfeit && margin > BLOWOUT_MARGIN) {
    flags.push({
      kind: 'blowout',
      detail: { margin, winner: derived.outcome },
    })
  }

  for (const t of input.teams) {
    // ⚠️ REGULATION, NOT THE DERIVED FINAL, AND THE DIFFERENCE IS A REAL FALSE
    // RAISE. `deriveResult` adds a goal to a shootout winner and awards 1-0 on a
    // forfeit — administrative goals that NO PLAYER SCORED and that therefore
    // can never appear in the roster marks. Compared against the final, a 2-2
    // game decided on penalties whose four scorers were all recorded correctly
    // raised `goals_unreconciled` on BOTH teams, and a forfeit raised it on any
    // team whose pre-abandonment goals the card still recorded. Regulation is
    // the only total a player mark can reconcile against. Found 2026-09-13,
    // while making the review form warn on the same rule: the form compared
    // against regulation and the record against the final, so a card could be
    // filed carrying a flag the reviewer was shown no warning about.
    const regulationFor = t.side === 'home' ? input.score.homeGoalsReg : input.score.visitorGoalsReg
    const marked = markedGoals(t.players)
    if (goalsUnreconciled(marked, regulationFor)) {
      flags.push({ kind: 'goals_unreconciled', detail: { teamId: t.teamId, marked, final: regulationFor } })
    }
    if (t.players.some((p) => p.card === 'red')) {
      flags.push({
        kind: 'red_card',
        detail: { teamId: t.teamId, children: t.players.filter((p) => p.card === 'red').map((p) => p.childId) },
      })
    }
    if (t.players.some((p) => p.injured)) {
      flags.push({
        kind: 'injury',
        detail: { teamId: t.teamId, children: t.players.filter((p) => p.injured).map((p) => p.childId) },
      })
    }
    if (t.players.some((p) => p.card === 'yellow')) {
      flags.push({
        kind: 'yellow_card',
        detail: { teamId: t.teamId, children: t.players.filter((p) => p.card === 'yellow').map((p) => p.childId) },
      })
    }
    const coach = t.coachCards ?? { yellow: 0, red: 0 }
    if (coach.yellow > 0 || coach.red > 0) {
      flags.push({ kind: 'coach_card', detail: { teamId: t.teamId, yellow: coach.yellow, red: coach.red } })
    }
    if ((t.incidentNote ?? '').trim().length > 0) {
      flags.push({ kind: 'incident_note', detail: { teamId: t.teamId } })
    }
    const injuryOrMisconduct = t.refTreatment?.injuryOrMisconduct === true
    const refereeMistreated = t.refTreatment?.refereeMistreated === true
    if (injuryOrMisconduct || refereeMistreated) {
      flags.push({ kind: 'incident_checked', detail: { teamId: t.teamId, injuryOrMisconduct, refereeMistreated } })
    }
    // Unreachable through the write routes since 2026-10-04: `/approve`,
    // `/approve-without-card`, `/override` and `/correct` refuse an unrated
    // line (`reviewRoutes.ts` `firstIncompleteMatrixField`). Kept, like `credit_unresolved`, so a
    // future write path that skips that check still leaves a visible flag.
    if (t.cellsRated < t.cellsTotal) {
      flags.push({
        kind: 'matrix_incomplete',
        detail: { teamId: t.teamId, rated: t.cellsRated, total: t.cellsTotal },
      })
    }
  }

  // ⚠️ THE CROSS-REFERENCE. The upload answer is the referee's own statement;
  // the review ticks are what the card shows. `onCard: false` is the case the
  // owner asked to chase (2026-09-27): reported at upload, not on the card.
  // Player question ↔ `injuryOrMisconduct`, referee question ↔
  // `refereeMistreated`, either team. A null answer was never asked.
  const up = input.uploadIncidents
  if (up && (up.player === true || up.referee === true)) {
    const ticked = (k: 'injuryOrMisconduct' | 'refereeMistreated') =>
      input.teams.some((t) => t.refTreatment?.[k] === true)
    const onCard =
      (up.player !== true || ticked('injuryOrMisconduct')) && (up.referee !== true || ticked('refereeMistreated'))
    flags.push({
      kind: 'incident_reported',
      detail: { player: up.player === true, referee: up.referee === true, onCard },
    })
  }

  // ⚠️ A CREDIT LINE THAT IS NOT FINISHED: no letter, or an A or C line with
  // no name, no person matched from the directory, or no credit
  // (`crewCreditGap`, owner rulings 2026-09-30). ONE kind, `credit_unresolved`,
  // whatever is missing, so the exception list has one issue type to filter on;
  // `reason` says which. The form and the write routes both refuse such a line,
  // so this fires only for a write that reached the DAO some other way.
  //
  // It absorbed two older flags: code C with no matched person (this kind, no
  // `reason`) and code A with a name the directory did not match (a
  // `crew_mismatch` carrying `reason: 'name_not_in_directory'`). Both are
  // `no_person` now, and rows raised before 2026-09-30 still carry the old
  // shapes.
  //
  // ⚠️ NO NAME IN `detail`. It is stored at rest and printed on an admin
  // screen; the slot names where to look and the card carries the handwriting.
  //
  // ⚠️ ONLY THE LINES THE PAYLOAD CARRIES. A slot it leaves out is not judged
  // here, unlike in `crewCreditGaps`: this reads the payload, not the record,
  // and on an override the DAO leaves an omitted slot's stored row alone, so
  // "absent" does not mean "no letter". The routes refuse an omitted slot.
  const forfeit = input.score.forfeitSide != null
  for (const { slot, gap } of crewCreditGaps(input.crew, { forfeit })) {
    if (input.crew.some((c) => c.slot === slot)) {
      flags.push({ kind: 'credit_unresolved', detail: { slot, reason: gap } })
    }
  }

  // ⚠️ THE CARD IS THE RECORD; THE SIGNUP IS EVIDENCE. This raises only the
  // clear case both sides can prove: the scheduler expected a NAMED person
  // for a position, the card names a DIFFERENT known person for the SAME
  // KIND of identity for the same position, and there is no reading of the
  // two under which they agree. Every other combination is a caller
  // admitting it does not know one side (or cannot safely compare the two
  // sides it has), and "we don't know" is not a disagreement — flagging it
  // would fire on routine gaps rather than genuine conflicts, training the
  // statistician to ignore the whole kind (see `BLOWOUT_MARGIN` and
  // `goals_unreconciled` above for the same under-raise instinct applied
  // elsewhere in this file).
  //
  //   - expectedCrew ABSENT (undefined): no evidence at all. Raise nothing.
  //   - expectedCrew EMPTY: the scheduler asserts there was nothing to
  //     expect (no slots for this game). Raise nothing.
  //   - a slot the scheduler never filled (no assignment row at all — BOTH
  //     `assigneeUserId` and `assigneeEmail` null) OR whose last assignment
  //     was 'cancelled' (backed out), where the card names someone: this is
  //     a walk-on covering a gap, not a disagreement — there was no
  //     expectation to contradict. Raise nothing.
  //   - a slot the scheduler filled, where the card names nobody for that
  //     position: could be a no-show, or just a reviewer who did not
  //     transcribe that line. Indistinguishable from here, so raise nothing
  //     rather than guess.
  //   - a slot whose card entry has no `resolvedPersonId`: the identity is
  //     unknown, so THIS COMPARISON has nothing to compare and stays quiet.
  //     ⚠️ That is not the same as nothing being raised: the crew loop above
  //     raises `credit_unresolved` / `no_person` for an A or C line nobody
  //     matched. Raising from here too would double-report one piece of
  //     handwriting.
  //   - `responsibility = 'Region'` (at-large): irrelevant here. That field
  //     names which TEAM gets sportsmanship/duty credit; this comparison is
  //     PERSON identity for the position, so an at-large slot compares
  //     exactly like a team-credited one.
  //   - the scheduler's identity for the slot and the card's resolved
  //     identity are of DIFFERENT KINDS (one an InLeague id, the other an
  //     email): raise nothing. `resolved_person_id` carries no kind of its
  //     own (`dao/crewSearch.ts`'s own `CrewPersonIdType` exists precisely
  //     because an id and an email are not reliably tellable apart on
  //     sight), so an id-shaped expectation compared against an
  //     email-shaped card value (or vice versa) could easily be the SAME
  //     PERSON expressed two different ways — guessing "different" there is
  //     exactly the over-raise this rule exists to avoid.
  //
  // `looksLikeEmail` is a comparison-safety heuristic, not an identity
  // system: every wrong guess it could make pushes towards staying quiet,
  // never towards a false raise, which is the one direction this rule is
  // allowed to be wrong in.
  //
  // ⚠️ AR1 AND AR2 ARE ONE PAIR (owner, 2026-09-29). An AR the card lists on
  // the other line from the one they signed up for did the job they signed
  // up for, so a card person who holds a live signup for the OTHER AR slot is
  // no mismatch. CR is not in the pair: CR and AR are different jobs, so a
  // CR/AR swap still raises. When only one of the two swapped, the slot a
  // different person worked still raises, and that flag is the real finding.
  const expected = input.expectedCrew ?? []
  const signedUpFor = (position: RefPosition | undefined, kind: 'id' | 'email', value: string) =>
    expected.some((o) => {
      if (o.position !== position) return false
      const id = expectedIdentity(o)
      return id?.kind === kind && id.value === value
    })

  for (const e of expected) {
    const identity = expectedIdentity(e)
    if (!identity) continue
    const { kind: expectedKind, value: expectedValue } = identity

    const cardPersonId = input.crew.find((c) => c.slot === e.position)?.resolvedPersonId ?? null
    if (!cardPersonId) continue
    const cardKind: 'id' | 'email' = looksLikeEmail(cardPersonId) ? 'email' : 'id'
    if (cardKind !== expectedKind) continue // different kinds — cannot safely compare, stay quiet

    if (cardPersonId !== expectedValue && !signedUpFor(otherArPosition(e.position), cardKind, cardPersonId)) {
      // ⚠️ NEVER PUT AN EMAIL IN `detail` — it is stored at rest in D1 and
      // read on an admin surface. An id is fine (it is opaque, like the team
      // ids every sibling flag already carries); an email is a real address,
      // so only `identifiedBy` records that the disagreement was email-kind,
      // and the statistician opens the card for the actual addresses.
      //
      // ⚠️ `identifiedBy` IS ALSO THIS BRANCH'S DISCRIMINATOR. Rows raised
      // before 2026-09-30 include a second `crew_mismatch` finding, a code-A
      // name the directory did not match, which carries `reason` and never
      // `identifiedBy`. This one always carries `identifiedBy` and never
      // `reason`.
      const detail: Record<string, unknown> = { slot: e.position, identifiedBy: expectedKind }
      if (expectedKind === 'id') {
        detail.expectedPersonId = expectedValue
        detail.cardPersonId = cardPersonId
      }
      flags.push({ kind: 'crew_mismatch', detail })
    }
  }

  if (input.reidentified) flags.push({ kind: 'reidentified', detail: {} })

  return flags
}
